import axios, { CanceledError } from 'axios';
import { ENV } from '@configs/env';
import { useAuthStore } from '@stores/auth.store';
import { refreshTokensSingleton } from '@services/refresh.service';
import { isPoolRecoveryInProgress, isTabBackgrounded, runSessionPoolRecovery } from '@utils/sessionNetworkCoordinator';

/**
 * Shared axios instance for all API calls.
 * On 401, single refresh + queued capped replay. Wake/idle recovery is coordinated in sessionNetworkCoordinator.
 */
const REQUEST_TIMEOUT_MS = 20000;
const MAX_INFLIGHT_BEFORE_RECOVERY = ENV.HTTP_MAX_INFLIGHT_BEFORE_RECOVERY;
const INFLIGHT_AGE_MS_BEFORE_RECOVERY = ENV.HTTP_INFLIGHT_AGE_MS_BEFORE_RECOVERY;
const RETRY_QUEUE_CONCURRENCY = 4;

const http = axios.create({
    baseURL: ENV.API_URL,
    withCredentials: true,
    timeout: REQUEST_TIMEOUT_MS,
});

let interceptorsInitialized = false;
let logoutInProgress = false;
const MAX_REFRESH_ATTEMPTS_PER_REQUEST = 2;
const SESSION_EXPIRED_FLAG = 'code360_session_expired';

const inflightRequests = new Map();

let wakeRequestController = typeof AbortController !== 'undefined' ? new AbortController() : null;

let isRefreshInProgress = false;
/** @type {{ resolve: (v: unknown) => void, reject: (e: unknown) => void, config: import('axios').InternalAxiosRequestConfig }[]} */
let refreshWaitQueue = [];

function composeAbortSignals(a, b) {
    if (!a) return b;
    if (!b) return a;
    if (a.aborted || b.aborted) {
        const dead = new AbortController();
        dead.abort();
        return dead.signal;
    }
    const merged = new AbortController();
    const onAbort = () => merged.abort();
    a.addEventListener('abort', onAbort, { once: true });
    b.addEventListener('abort', onAbort, { once: true });
    return merged.signal;
}

/** Drop stuck shared `http` requests (logout, session expired, pool recovery). */
export function abortAllSharedHttpInflight() {
    if (wakeRequestController) {
        try {
            wakeRequestController.abort();
        } catch {
            // ignore
        }
        wakeRequestController = new AbortController();
    }
    inflightRequests.clear();
}

function trackInflightAbortCleanup(config) {
    const requestId = config?._requestId;
    const signal = config?.signal;
    if (!requestId || !signal?.addEventListener) return;
    signal.addEventListener(
        'abort',
        () => {
            inflightRequests.delete(requestId);
        },
        { once: true },
    );
}

function rejectRefreshWaitQueue(error) {
    const queue = refreshWaitQueue;
    refreshWaitQueue = [];
    isRefreshInProgress = false;
    for (const item of queue) {
        item.reject(error);
    }
}

async function replayRefreshWaitQueue(accessToken) {
    const queue = refreshWaitQueue;
    refreshWaitQueue = [];

    if (!queue.length) return;

    let index = 0;
    const worker = async () => {
        while (index < queue.length) {
            const current = index;
            index += 1;
            const { resolve, reject, config } = queue[current];
            try {
                config._retry = true;
                config.headers = config.headers || {};
                config.headers.Authorization = `Bearer ${accessToken}`;
                resolve(await http(config));
            } catch (err) {
                reject(err);
            }
        }
    };

    const workers = Math.min(RETRY_QUEUE_CONCURRENCY, queue.length);
    await Promise.all(Array.from({ length: workers }, () => worker()));
}

function enqueueRequestForRefreshReplay(originalRequest) {
    return new Promise((resolve, reject) => {
        refreshWaitQueue.push({ resolve, reject, config: originalRequest });
    });
}

function maybeSoftRecoverInflightQueue() {
    // Don't trigger a new soft recovery if one is already running.
    if (isTabBackgrounded() || isPoolRecoveryInProgress()) return;
    if (inflightRequests.size <= MAX_INFLIGHT_BEFORE_RECOVERY) return;

    let oldest = Date.now();
    for (const startedAt of inflightRequests.values()) {
        if (typeof startedAt === 'number' && startedAt < oldest) oldest = startedAt;
    }
    const age = Date.now() - oldest;
    if (age < INFLIGHT_AGE_MS_BEFORE_RECOVERY) return;

    void runSessionPoolRecovery({ reason: 'soft-inflight' });
}

/** Auth routes that must NOT send access-token Bearer (login, signup, refresh, etc.). */
function isAuthExcludedFromBearer(url = '') {
    const u = String(url || '');
    const isAuthRoute = /\/auth\//i.test(u);
    if (!isAuthRoute) return false;
    /** Logged-in session routes under /auth/* that still require Bearer (see API docs). */
    const isProtectedAuthRoute =
        /\/auth\/(me|check|verify-password|modify-password)\/?$/i.test(u);
    return !isProtectedAuthRoute;
}

function shouldRetryWithRefresh(error, originalRequest, isLoginRequest, hasRefreshToken) {
    if (!originalRequest || !hasRefreshToken || isLoginRequest) return false;
    if (isAuthExcludedFromBearer(originalRequest?.url || '')) return false;
    const isRefreshRequest = /\/auth\/refresh\/?$/i.test(originalRequest?.url || '');
    if (isRefreshRequest) return false;
    const status = error.response?.status;
    const is401 = status === 401;
    const is403 = status === 403;
    const msgRaw =
        error?.response?.data?.message ??
        error?.response?.data?.error ??
        error?.message ??
        '';
    const msg = String(msgRaw).toLowerCase();
    const looksLikeExpiredToken =
        msg.includes('token') && (msg.includes('expired') || msg.includes('jwt') || msg.includes('unauthorized'));
    const attempts = originalRequest._refreshAttempt || 0;
    if (attempts >= MAX_REFRESH_ATTEMPTS_PER_REQUEST) return false;
    return is401 || (is403 && looksLikeExpiredToken);
}

function shouldRetryTimeoutWithRefresh(error, originalRequest, isLoginRequest, hasRefreshToken) {
    if (!originalRequest || !hasRefreshToken || isLoginRequest) return false;
    if (isAuthExcludedFromBearer(originalRequest?.url || '')) return false;
    if ((originalRequest._timeoutRefreshAttempt || 0) >= MAX_REFRESH_ATTEMPTS_PER_REQUEST) return false;

    const isTimeout =
        error?.code === 'ECONNABORTED' ||
        String(error?.message || '').toLowerCase().includes('timeout') ||
        String(error?.message || '').toLowerCase().includes('timed out');
    if (!isTimeout) return false;

    const status = error.response?.status;
    if (status === 401 || status === 403) return true;

    const msgRaw =
        error?.response?.data?.message ??
        error?.response?.data?.error ??
        error?.message ??
        '';
    const msg = String(msgRaw).toLowerCase();
    const looksLikeExpiredToken =
        msg.includes('token') && (msg.includes('expired') || msg.includes('jwt') || msg.includes('unauthorized'));
    if (looksLikeExpiredToken) return true;

    return (originalRequest._refreshAttempt || 0) > 0;
}

async function refreshSessionAndReplayQueue(doSessionExpiredLogoutOnce) {
    let newTokens = null;
    try {
        newTokens = await refreshTokensSingleton(
            () => useAuthStore.getState().refreshToken,
            useAuthStore.setState,
            doSessionExpiredLogoutOnce,
        );
    } catch (err) {
        rejectRefreshWaitQueue(err);
        throw err;
    }

    if (!newTokens?.accessToken) {
        const err = new Error('Refresh failed');
        rejectRefreshWaitQueue(err);
        return null;
    }

    import('@services/realtime.socket')
        .then((m) => m.reconnectWithNewToken?.())
        .catch(() => {});

    await replayRefreshWaitQueue(newTokens.accessToken);
    return newTokens.accessToken;
}

export const initializeHttpInterceptors = () => {
    if (interceptorsInitialized) return;
    interceptorsInitialized = true;

    http.interceptors.request.use((config) => {
        // During pool recovery, immediately reject new requests (not retries).
        // React Query will mark them as failed; refetchActiveQueriesBatched fires
        // them cleanly one-by-one after recovery completes.
        if (isPoolRecoveryInProgress() && !config._retry) {
            return Promise.reject(new CanceledError('HTTP paused for session recovery'));
        }

        const requestId = `${Date.now()}_${Math.random().toString(16).slice(2)}`;
        config._requestId = requestId;
        inflightRequests.set(requestId, Date.now());
        maybeSoftRecoverInflightQueue();

        if (wakeRequestController?.signal) {
            config.signal = composeAbortSignals(config.signal, wakeRequestController.signal);
        }
        trackInflightAbortCleanup(config);

        const isAuthRouteRequest = isAuthExcludedFromBearer(config?.url || '');
        if (config._retry && config.headers?.Authorization) return config;
        if (isAuthRouteRequest) return config;
        const token = useAuthStore.getState().accessToken;
        if (token) {
            config.headers = config.headers || {};
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    });

    http.interceptors.response.use(
        (response) => {
            try {
                const requestId = response?.config?._requestId;
                if (requestId) inflightRequests.delete(requestId);
            } catch {
                // ignore
            }
            return response;
        },
        async (error) => {
            // Requests cancelled during pool recovery (CanceledError) propagate here.
            // React Query handles them as cancelled; refetchActiveQueriesBatched
            // replays them after recovery completes.
            if (axios.isCancel(error) && isPoolRecoveryInProgress()) {
                return Promise.reject(error);
            }

            const originalRequest = error.config;
            if (!originalRequest) return Promise.reject(error);

            const isLoginRequest = /\/auth\/login\/?$/i.test(originalRequest?.url || '');
            const hasRefreshToken = !!useAuthStore.getState().refreshToken;

            try {
                const requestId = originalRequest?._requestId;
                if (requestId) inflightRequests.delete(requestId);
            } catch {
                // ignore
            }
            maybeSoftRecoverInflightQueue();

            const shouldRefreshForAuth = shouldRetryWithRefresh(error, originalRequest, isLoginRequest, hasRefreshToken);
            const shouldRefreshForTimeout = shouldRetryTimeoutWithRefresh(
                error,
                originalRequest,
                isLoginRequest,
                hasRefreshToken,
            );

            if (shouldRefreshForAuth || shouldRefreshForTimeout) {
                if (shouldRefreshForAuth) {
                    originalRequest._refreshAttempt = (originalRequest._refreshAttempt || 0) + 1;
                }
                if (shouldRefreshForTimeout) {
                    originalRequest._timeoutRefreshAttempt = (originalRequest._timeoutRefreshAttempt || 0) + 1;
                }

                const doSessionExpiredLogoutOnce = () => {
                    if (logoutInProgress) return;
                    logoutInProgress = true;
                    try {
                        sessionStorage.setItem(SESSION_EXPIRED_FLAG, '1');
                    } catch {
                        // ignore
                    }
                    Promise.resolve(useAuthStore.getState().hardLogout?.() ?? useAuthStore.getState().logout?.())
                        .finally(() => {
                            logoutInProgress = false;
                        });
                };

                if (isRefreshInProgress) {
                    return enqueueRequestForRefreshReplay(originalRequest);
                }

                isRefreshInProgress = true;

                try {
                    const accessToken = await refreshSessionAndReplayQueue(doSessionExpiredLogoutOnce);
                    if (!accessToken) {
                        doSessionExpiredLogoutOnce();
                        return Promise.reject(new Error('Refresh failed'));
                    }

                    originalRequest._retry = true;
                    originalRequest.headers = originalRequest.headers || {};
                    originalRequest.headers.Authorization = `Bearer ${accessToken}`;
                    return await http(originalRequest);
                } catch (err) {
                    console.error('Token refresh error:', err);
                    const status = err?.status ?? err?.response?.status;
                    const isNetworkOrAbortError =
                        !status ||
                        status === 502 ||
                        status === 503 ||
                        status === 504 ||
                        err?.name === 'AbortError' ||
                        err?.name === 'CanceledError' ||
                        String(err?.message || '').toLowerCase().includes('aborted') ||
                        err?.message === 'Network Error' ||
                        err?.code === 'ERR_NETWORK';

                    if (!isNetworkOrAbortError) {
                        const authAttemptsExhausted =
                            (originalRequest._refreshAttempt || 0) >= MAX_REFRESH_ATTEMPTS_PER_REQUEST;
                        const timeoutAttemptsExhausted =
                            (originalRequest._timeoutRefreshAttempt || 0) >= MAX_REFRESH_ATTEMPTS_PER_REQUEST;
                        if (authAttemptsExhausted || timeoutAttemptsExhausted) {
                            doSessionExpiredLogoutOnce();
                        }
                    }
                    return Promise.reject(err);
                } finally {
                    isRefreshInProgress = false;
                }
            }

            return Promise.reject(error);
        },
    );
};

initializeHttpInterceptors();

export default http;